import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaFileAlt, FaFilePdf } from "react-icons/fa";
import styles from "./StudyMaterial.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import CustomTextInput from "../../../../components/CustomTextInput/CustomTextInput";

function ViewStudyMaterial() {
  const navigate = useNavigate();
  const location = useLocation();
  const { translations } = useSelector((state) => state.language);

  // Auth / user context
  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const user_id = user?.id || "";
  const slug = String(user?.role?.slug || "").toLowerCase();
  const navigateUser = slug === "staff" ? "/user/assessmentList" : "/user/assessment";
  // Coming from list via navigate(..., { state: { ID, data } })
  const rowData = location?.state?.data || {};
  const assessmentId = rowData?.assessment_id;
  const assessment_id = location?.state?.assessment_id

  const bookId = rowData?.book_id;
  const book_id = rowData?.book_id;

  const studentId = rowData?.student_id || user_id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const contentRef = useRef(null);

  useEffect(() => {
    fetchStudyMaterial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId, studentId, bookId]);

  const fetchStudyMaterial = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        school_id: school_id,
        assessment_id: assessmentId || assessment_id,
        student_id: studentId,
        book_id: bookId,
      };

      const response = await AxiosInstance.post(
        `/book/studyMaterial/view`,
        payload,
      );

      if (response?.data?.status) {
        setData(response.data.data);
      } else {
        setError(response?.data?.message || "Failed to load study material");
      }
    } catch (err) {
      console.error("Error loading study material:", err);
      setError(
        err?.response?.data?.message || err?.message || "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(navigateUser, { state: { activeTab: 2 } });
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);

    try {
      const payload = {
        school_id: school_id,
        assessment_id: assessmentId ||assessment_id,
        student_id: studentId,
      };

      const response = await AxiosInstance.post(
        `/book/downloadStudyMaterial`,
        payload,
        {
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], { type: "application/pdf" });

      const { study_material, assessment } = data || {};
      const topic =
        study_material?.topic || assessment?.topic || "StudyMaterial";
      const safeTopic = topic
        .replace(/[^a-z0-9]+/gi, "_")
        .replace(/^_|_$/g, "");

      let fileName = `${safeTopic || "StudyMaterial"}.pdf`;

      const contentDisposition = response.headers?.["content-disposition"];
      if (contentDisposition) {
        const match = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
        );
        if (match && match[1]) {
          fileName = match[1].replace(/['"]/g, "").trim();
        }
      }

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(objectUrl), 100);
    } catch (err) {
      console.error("Error downloading PDF:", err);

      let errorMessage = "Failed to download PDF. Please try again.";
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          errorMessage = json.message || errorMessage;
        } catch (_) {
          // not JSON, keep default message
        }
      } else {
        errorMessage =
          err?.response?.data?.message || err?.message || errorMessage;
      }

      alert(errorMessage);
    } finally {
      setDownloading(false);
    }
  };

  const sanitizedContent = useMemo(() => {
    const raw = data?.study_material?.content || "";
    return raw
      .replace(/&nbsp;/gi, " ") // entity form
      .replace(/\u00A0/g, " "); // already-decoded char form
  }, [data]);

  if (loading) {
    return (
      <div className={styles.loaderWrapper}>
        <div className={styles.spinner}></div>
        <p>{translations?.labels?.loading || "Loading study material..."}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorWrapper}>
        <p>⚠ {error}</p>
        <button onClick={handleBack} className={styles.backButton}>
          {translations?.labels?.back || "Back"}
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { study_material, assessment } = data;

  // Field values for the Book Details section
  const bookValue = assessment?.book_name?.trim() || "-";
  const classValue = assessment?.class_name || assessment?.class || "-";
  const sectionValue = assessment?.section_id || "-";
  const mediumValue =
    assessment?.medium === "TM"
      ? "Tamil"
      : assessment?.medium === "EM"
        ? "English"
        : assessment?.medium || "-";
  const topicValue = study_material?.topic || assessment?.topic || "-";
  const subTopicValue =
    study_material?.sub_topic || assessment?.sub_topic || "-";

  return (
    <div className={styles.pageWrapper}>
      {/* Top right: Download as PDF button */}
      <div className={styles.topActions}>
        <button
          type="button"
          onClick={handleDownloadPDF}
          className={styles.downloadButton}
          disabled={downloading}
        >
          <FaFilePdf className={styles.btnIcon} />
          {downloading
            ? translations?.labels?.downloading || "Downloading..."
            : translations?.labels?.downloadPdf || "Download as PDF"}
        </button>
      </div>

      {/* Book Details Card */}
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaFileAlt className={styles.headerIcon} />
          </div>
          <h1 className={styles.headerTitle}>
            {translations?.labels?.bookDetails || "Book Details"}
          </h1>
        </div>
        <div className={styles.fieldsGrid}>
          <CustomTextInput
            label={translations?.labels?.book || "Book"}
            value={bookValue}
            disabled
            readOnly
            style={{ marginBottom: 4 }}
          />
          <CustomTextInput
            label={translations?.labels?.class || "Class"}
            value={classValue}
            disabled
            readOnly
            style={{ marginBottom: 4 }}
          />
          <CustomTextInput
            label={translations?.labels?.section || "Section"}
            value={sectionValue}
            disabled
            readOnly
            style={{ marginBottom: 4 }}
          />
          <CustomTextInput
            label={translations?.labels?.medium || "Medium"}
            value={mediumValue}
            disabled
            readOnly
            style={{ marginBottom: 4 }}
          />
          <CustomTextInput
            label={translations?.labels?.topic || "Topic"}
            value={topicValue}
            disabled
            readOnly
            style={{ marginBottom: 4 }}
          />
          <CustomTextInput
            label={translations?.labels?.subTopic || "Sub Topic"}
            value={subTopicValue}
            disabled
            readOnly
            style={{ marginBottom: 4 }}
          />
        </div>
      </div>

      {/* View Study Material Card */}
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaFileAlt className={styles.headerIcon} />
          </div>
          <h1 className={styles.headerTitle}>
            {translations?.labels?.viewStudyMaterial || "View Study Material"}
          </h1>
        </div>
        <div
          ref={contentRef}
          className={styles.studyContent}
          dangerouslySetInnerHTML={{
            __html: sanitizedContent,
          }}
        />
      </div>

      {/* Footer with Back button */}
      <div className={styles.footer}>
        <button
          type="button"
          onClick={handleBack}
          className={styles.backButton}
        >
          {translations?.labels?.back || "Back"}
        </button>
      </div>
    </div>
  );
}

export default ViewStudyMaterial;
